import { useCloseDeletedSelection } from "../../sites/components/common/useCloseDeletedSelection";
import { useEffect, useMemo, useRef, useState } from "react";
import { SystemAdminService } from "../../services/SystemAdminService";
import { FormControlElement, IComponentPropertyType } from "../../models/Interfaces";
import Loading from "../../sites/components/common/Loading";
import { Core } from "@form-validation/bundle/full";
import { CellProps, Column } from "react-table";
import { Button, Col, Form, FormControl, FormLabel, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faSave, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useConfirm } from "../../config/ConfirmProvider";
import FilteredTable from "../../sites/components/common/FilteredTable";
import AppModal from "../../sites/components/common/AppModal";
import { createFormValidation, isFormValid } from "../../helpers/formValidationHelper";
import styles from "./componentAdmin.module.scss";

const ComponentPropertyTypes = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [propertyTypes, setPropertyTypes] = useState<Array<IComponentPropertyType>>([]);
  const [selectedType, setSelectedType] = useState<IComponentPropertyType | null>(null);
  const [showDetail, setShowDetail] = useState<boolean>(false);

  const sysAdmSvcRef = useRef<SystemAdminService | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const fvRef = useRef<Core | null>(null);

  const confirmation = useConfirm();

  const columns: Column<IComponentPropertyType>[] = useMemo(() => [
    {
      Header: "Template Name",
      accessor: "Name"
    },
    {
      Header: "Base Type",
      accessor: "Type"
    },
    {
      id: "Actions",
      Header: "",
      Cell: ({ row }: CellProps<IComponentPropertyType>) => (
        <div>
          <Button variant="outline-primary" onClick={() => editPropertyType(row.original)} className="btn-nomin me-2" title="Edit property type">
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Button variant="outline-danger" onClick={() => deletePropertyType(row.original)} className="btn-nomin" title="Delete proeprty type">
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      )
    }
  ], [selectedType, propertyTypes]);

  const getPropertyTypes = async (): Promise<void> => {
    if (sysAdmSvcRef.current) {
      const res = await sysAdmSvcRef.current.getComponentPropertyTypes();
      setPropertyTypes(res);
    }
  };

  const editPropertyType = (type: IComponentPropertyType): void => {
    if (type) {
      setSelectedType(type);
      setShowDetail(true);
    }
  };

  const closeDeletedSelection = useCloseDeletedSelection(selectedType, (item) => item.Id, () => closeDetail());

  const deletePropertyType = async (type: IComponentPropertyType): Promise<void> => {
    if (type && sysAdmSvcRef.current) {
      const confirmed = await confirmation.requestConfirmation(`Delete <b>${type.Name}</b> from component property types?<br /><b>This action cannot be undone.</b>`, "Delete Property Type");

      if (confirmed) {
        const delres = await sysAdmSvcRef.current.deletePropertyType(type.Id);

        if (delres) {
          setPropertyTypes(prev => prev.filter(pt => pt.Id !== type.Id));
          closeDeletedSelection(type.Id);
        }
      }
    }
  };

  const addPropertyType = (): void => {
    if (sysAdmSvcRef.current) {
      setSelectedType(sysAdmSvcRef.current.newPropertyType());
      setShowDetail(true);
    }
  };

  const savePropertyType = async () : Promise<void> => {
    if (selectedType && fvRef.current && sysAdmSvcRef.current) {
      const validres = await isFormValid(fvRef.current);
      if (validres) {
        const saveres = await sysAdmSvcRef.current.savePropertyType(selectedType);

        if (saveres) {
          const existing = propertyTypes.find(pt => pt.Id === saveres.Id);
          if (existing) {
            setPropertyTypes(prev => prev.map(pt => pt.Id === saveres.Id ? saveres : pt));
          } else {
            setPropertyTypes(prev => [
              ...prev,
              saveres
            ]);
          }
          closeDetail();
        }
      }
    }
  };

  const closeDetail = (): void => {
    setSelectedType(null);
    setShowDetail(false);
  };

  const handleInputChange = (event: React.ChangeEvent<FormControlElement>) : void => {
    const {name, value} = event.target;

    if (selectedType && name) {
      setSelectedType(prev => ({
        ...prev as IComponentPropertyType,
        [name]: value
      }));
    }
  };

  const createValidator = () : void => {
    if (formRef.current && !fvRef.current) {
      fvRef.current = createFormValidation(formRef.current, {
        Name: {
          validators: {
            notEmpty: { message: "property template/type name is required"}
          }
        },
        Type: {
          validators: {
            notEmpty: { message: "property base type is required"}
          }
        }
      });
    }
  };

  useEffect(() => {
    if (showDetail) {
      createValidator();
    } else {
      fvRef.current = null;
    }
  }, [showDetail]);

  useEffect(() => {
    if (!sysAdmSvcRef.current) {
      sysAdmSvcRef.current = new SystemAdminService();
    }

    const getData = async () => {
      try {
        await Promise.all([
          getPropertyTypes()
        ]);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }

    getData();

  }, []);

  return (
    <>
      {loading && (
        <Loading Message="Loading property types..." SpinSize="5x" />
      )}

      {!loading && (
        <>
          <section className={`app-list-hero ${styles.systemSettingsHero}`}>
            <div>
              <p className="app-list-hero__eyebrow">System Administration</p>
              <h1 className="app-list-hero__title">Component Property Types</h1>
              <p className="app-list-hero__summary">Manage the property type definitions available to components across the CMS.</p>
            </div>
            <Button variant="primary" className={styles.primaryButton} onClick={addPropertyType}>
              <FontAwesomeIcon icon={faPlus} />
              <span>Add a property type</span>
            </Button>
          </section>
          <div className="app-list-section">
            <div className="app-list-section__body">
              <FilteredTable<IComponentPropertyType>
                columns={columns}
                data={propertyTypes}
                noDataMessage="No property types found, click the button to add"
              />
            </div>
          </div>
        </>
      )}

      {showDetail && selectedType && (
        <AppModal
          onClose={closeDetail}
          showButtons={true}
          size="md"
          actionButtonVariant="success"
          cancelButtonVariant="primary"
          cancelButtonText="Cancel"
          dataActionButtonText="Save"
          actionButtonIcon={faSave}
          title="Site Meta Data"
          onDataAction={() => savePropertyType()}>
            <Form id="PropertyTypeForm" ref={formRef}>
              <Row>
                <Col md={12} className="mt-3 fv-row">
                  <FormLabel htmlFor="Name">Template Name</FormLabel>
                  <FormControl
                    type="text"
                    value={selectedType.Name}
                    onChange={handleInputChange}
                    name="Name"
                    id="Name"
                    placeholder="set property name, matching an editor template"
                    title="set property template name"
                  />
                  <div className="fv-validation-message"></div>
                </Col>
              </Row>
              <Row>
                <Col md={12} className="mt-3 fv-row">
                  <FormLabel htmlFor="Type">Base property type</FormLabel>
                  <FormControl
                    type="text"
                    value={selectedType.Type}
                    onChange={handleInputChange}
                    name="Type"
                    id="Type"
                    placeholder="set property base type (data storage type)"
                    title="set property base type"
                  />
                  <div className="fv-validation-message"></div>
                </Col>
              </Row>              
            </Form>
        </AppModal>
      )}
    </>
  );
}

export default ComponentPropertyTypes;
